/**
 * Tenant Licenses Tab Component - Admin Portal
 * Manages tenant seat pools (NOT user assignments)
 * User license assignment is done in CRM, not Admin Portal
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  Key,
  Plus,
  Minus,
  Pencil,
  Trash2,
  Loader2,
  DollarSign,
  AlertCircle,
  Users,
  Package,
  Info
} from 'lucide-react';
import axios from 'axios';
import {
  resolveView, VIEW, seats, seatInconsistency, editionLabel, resolveSourceFailure,
  priceLabel, lineTotalLabel, monthlyCost, isTenantLevel, seatTotals, TENANT_LEVEL_LABEL,
  addableCatalogue, catalogueOptionLabel, defaultPriceLabel, licenceProductText,
  availabilityText,
} from './licencePresentation';
import { PermissionGate } from '../components';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

const TenantLicensesTab = ({ tenantId, getAdminToken }) => {
  const [licenses, setLicenses] = useState([]);
  const [catalogLicenses, setCatalogLicenses] = useState([]);
  const [catalogueFailed, setCatalogueFailed] = useState(false);
  const [commercialState, setCommercialState] = useState(null);
  const [commercialStateFailed, setCommercialStateFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileStarted, setReconcileStarted] = useState(false);

  const [formData, setFormData] = useState({
    license_id: '',
    seats_purchased: 1,
    override_price: null,
    use_override: false
  });

  const headers = { Authorization: `Bearer ${getAdminToken()}` };

  const fetchLicenses = useCallback(async () => {
    try {
      setLoading(true);
      // TWO INDEPENDENT READS, SETTLED INDEPENDENTLY. These used to be
      // Promise.all'd, so a licence-CATALOGUE outage blanked the tenant's own
      // licences even though they had loaded perfectly well. The catalogue only
      // supplies edition LABELS; losing it must degrade a column, not the page.
      const [tenantLicRes, catalogRes, commercialRes] = await Promise.allSettled([
        axios.get(`${API}/tenants/${tenantId}/licenses`, { headers }),
        axios.get(`${API}/license-catalog`, { headers, params: { active_only: true } }),
        axios.get(`${API}/tenants/${tenantId}/commercial-summary`, { headers }),
      ]);
      if (tenantLicRes.status === 'rejected') throw tenantLicRes.reason;
      setLicenses(tenantLicRes.value.data || []);
      // A missing catalogue leaves editions showing their internal value,
      // which is true and merely less pretty.
      setCatalogLicenses(
        catalogRes.status === 'fulfilled' ? (catalogRes.value.data.licenses || []) : []
      );
      setCatalogueFailed(catalogRes.status === 'rejected');
      setCommercialState(
        commercialRes.status === 'fulfilled' ? commercialRes.value.data : null);
      setCommercialStateFailed(commercialRes.status === 'rejected');
      setError(null);
    } catch (err) {
      setError('Failed to load licenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `headers` is derived purely from getAdminToken, already listed
  }, [tenantId, getAdminToken]);

  const handleReconcile = async () => {
    try {
      setReconciling(true);
      setError(null);
      await axios.post(`${API}/tenants/${tenantId}/licenses/reconcile`, {}, { headers });
      setReconcileStarted(true);
      // The endpoint intentionally schedules the work. Re-read once after the
      // worker has had a chance to materialise the rows; Refresh remains a
      // deterministic operator action if a large catalogue takes longer.
      window.setTimeout(() => fetchLicenses(), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start licence reconciliation');
    } finally {
      setReconciling(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const handleAddLicense = async () => {
    try {
      setSaving(true);
      const data = {
        license_id: formData.license_id,
        // A tenant-level licence is one per workspace; the dialog offers no
        // seat count for it, and the server holds it at one regardless.
        seats_purchased: addingTenantLevel ? 1 : formData.seats_purchased,
        override_price: formData.use_override ? formData.override_price : null
      };
      await axios.post(`${API}/tenants/${tenantId}/licenses`, data, { headers });
      setShowAddDialog(false);
      resetForm();
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add license');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLicense = async () => {
    try {
      setSaving(true);
      const data = {
        // No seat count for a tenant-level licence: there is none to edit.
        ...(isTenantLevel(selectedLicense) ? {} : { seats_purchased: formData.seats_purchased }),
        override_price: formData.use_override ? formData.override_price : null
      };
      await axios.patch(`${API}/tenants/${tenantId}/licenses/${selectedLicense.id}`, data, { headers });
      setShowEditDialog(false);
      setSelectedLicense(null);
      resetForm();
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update license');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLicense = async (license) => {
    if (license.seats_assigned > 0) {
      setError(`Cannot remove license - ${license.seats_assigned} seats are assigned to users`);
      return;
    }
    if (!window.confirm(`Remove "${license.license_name}" from this tenant?`)) return;

    try {
      await axios.delete(`${API}/tenants/${tenantId}/licenses/${license.id}`, { headers });
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove license');
    }
  };

  const openEditDialog = (license) => {
    setSelectedLicense(license);
    setFormData({
      license_id: license.license_id,
      seats_purchased: license.seats_purchased,
      override_price: license.override_price || license.default_price_snapshot,
      use_override: license.override_price != null
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      license_id: '',
      seats_purchased: 1,
      override_price: null,
      use_override: false
    });
  };

  // Licences this tenant may still be given: not held, and not a seat that
  // now comes with another product (Form Builder / Survey Builder with
  // Engage) — see `addableCatalogue`.
  const availableLicenses = addableCatalogue(catalogLicenses, licenses);
  // The catalogue row being added, if it is held by the workspace.
  const addingTenantLevel = isTenantLevel(
    catalogLicenses.find((cl) => cl.id === formData.license_id));

  // Calculate totals — SEATS ARE PEOPLE, so a tenant-level licence (AI Calling,
  // Cluvik AI) is not counted as one. See `seatTotals`.
  const { purchased: totalSeats, assigned: totalAssigned } = seatTotals(licenses);
  // AN UNPRICED LICENCE IS NOT A FREE ONE — see `monthlyCost`.
  const { total: monthlyTotal, unpricedCount } = monthlyCost(licenses);
  // ONE view decision for the whole tab, cards included.
  //
  // The table said "Cluvik could not load this tenant's licences" while the four
  // cards above it read License Types 0, Total Seats 0, Seats Assigned 0 — the
  // same screen asserting both "we don't know" and "there are none". A failed
  // load has no numerator, so the cards show a dash. Measured live on 5609b9bb.
  const view = resolveView({ loading, error, licences: licenses });
  const unknown = view === VIEW.ERROR || view === VIEW.LOADING;
  const card = (value) => (unknown ? '\u2014' : value);

  return (
    <div className="space-y-6" data-testid="tenant-licenses-tab">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">License Types</p>
                <p className="text-2xl font-bold">{card(licenses.length)}</p>
              </div>
              <Package className="h-8 w-8 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Seats</p>
                <p className="text-2xl font-bold">{card(totalSeats)}</p>
              </div>
              <Key className="h-8 w-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Seats Assigned</p>
                <p className="text-2xl font-bold">{card(totalAssigned)}</p>
              </div>
              <Users className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Monthly Cost</p>
                <p className="text-2xl font-bold">
                  {unknown ? '\u2014' : unpricedCount > 0
                    ? (unpricedCount === licenses.length ? 'Not priced' : `$${monthlyTotal.toFixed(2)} known`)
                    : `$${monthlyTotal.toFixed(2)}`}
                </p>
                {!unknown && unpricedCount > 0 && (
                  <p className="text-xs text-slate-500" data-testid="licence-unpriced-note">
                    {unpricedCount} licence{unpricedCount === 1 ? '' : 's'} not priced
                  </p>
                )}
              </div>
              <DollarSign className="h-8 w-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Note */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Seat Pool Management</p>
            <p className="text-sm text-blue-700 mt-1">
              This screen manages the tenant's seat pool. To assign licenses to individual users, 
              use the CRM User Management within the tenant's application.
            </p>
          </div>
        </div>
      </div>

      {/* Licenses Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="h-4 w-4" />
              License Subscriptions
            </CardTitle>
            <CardDescription>
              Manage seat allocations and pricing for this tenant
            </CardDescription>
          </div>
          <Button
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="bg-indigo-600 hover:bg-indigo-700"
            disabled={availableLicenses.length === 0}
            data-testid="add-license-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add License
          </Button>
        </CardHeader>
        <CardContent>
          {/* Enrichment lost, commercial truth intact. The catalogue supplies
              edition LABELS only, so losing it costs a prettier word and the
              table stands — blanking loaded licences because a label lookup
              failed is how a control-plane surface tells an operator a tenant
              owns nothing. */}
          {!error && resolveSourceFailure({ licencesFailed: false, catalogueFailed })?.message && (
            <div className="text-xs text-amber-700 mb-3" data-testid="licenses-enrichment-warning">
              {resolveSourceFailure({ licencesFailed: false, catalogueFailed }).message}
            </div>
          )}
          {/* FOUR STATES, NEVER THREE. This read
              `loading ? spinner : licenses.length === 0 ? "No licenses" : table`,
              and a FAILED FETCH leaves the array empty — so a tenant whose
              licences could not be loaded was reported as a tenant that has
              none. Those are opposite facts about a commercial record, and the
              wrong one invites an operator to add a licence the tenant already
              owns. `resolveView` puts error above emptiness, always. */}
          {view === VIEW.LOADING ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
            </div>
          ) : view === VIEW.ERROR ? (
            <div className="text-center py-8 text-amber-700" data-testid="licenses-error-state">
              Cluvik could not load this tenant's licences. This is not the same
              as the tenant having none — nothing has been changed.
            </div>
          ) : view === VIEW.EMPTY ? (
            <div className="text-center py-8 text-slate-500" data-testid="licenses-empty-state">
              {commercialState?.subscription?.status === 'active' ? (
                <>
                  <p className="font-medium text-amber-800">
                    Active agreement, but no materialised licences
                  </p>
                  <p className="text-sm mt-1 max-w-xl mx-auto">
                    The agreement and seat pool are separate records. Reconcile derives the
                    missing licence rows from the active agreement without changing products
                    or withdrawing access.
                  </p>
                  <PermissionGate permission="tenant:update">
                    <Button variant="outline" size="sm" className="mt-3"
                            onClick={handleReconcile} disabled={reconciling}
                            data-testid="reconcile-empty-licenses">
                      {reconciling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Reconcile from agreement
                    </Button>
                  </PermissionGate>
                  {reconcileStarted && (
                    <p className="text-xs text-emerald-700 mt-2">
                      Reconciliation started. This table will refresh automatically.
                    </p>
                  )}
                </>
              ) : commercialStateFailed ? (
                <>
                  <p className="font-medium text-amber-800">
                    No materialised licences; agreement state unavailable
                  </p>
                  <p className="text-sm mt-1">
                    The licence read succeeded, but Cluvik could not confirm whether an
                    active agreement should materialise licence rows. Refresh before reconciling.
                  </p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={fetchLicenses}>
                    Refresh
                  </Button>
                </>
              ) : (
                <>No licences are materialised for this tenant, and no active agreement was found.</>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>License</TableHead>
                  {/* The internal edition is what distinguishes a free Data
                      Manager from a pro one; the tab showed neither. */}
                  <TableHead>Edition</TableHead>
                  <TableHead>Seats</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead>Price/Seat</TableHead>
                  <TableHead>Monthly Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licenses.map((license) => (
                  <TableRow key={license.id} data-testid={`tenant-license-row-${license.license_code}`}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{license.license_name}</div>
                        {/* The product, by name — never the licence code
                            (CHATBOT_SEAT, CLUVIK_AI_TENANT), which is an
                            identifier, not something an operator reads. */}
                        {licenceProductText(license) && (
                          <div className="text-xs text-slate-500"
                               data-testid={`licence-product-${license.id}`}>
                            {licenceProductText(license)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell data-testid={`licence-edition-${license.license_code}`}>
                      {editionLabel(license, catalogLicenses)
                        ? <Badge variant="outline">{editionLabel(license, catalogLicenses)}</Badge>
                        /* No edition is NOT "Basic". Inventing a label
                           describes a tenant as something nobody sold it. */
                        : <span className="text-slate-400">—</span>}
                    </TableCell>
                    {/* A TENANT-LEVEL LICENCE IS NOT A SEAT POOL. AI Calling
                        read "1 seats · 1 used · 0 free" here (tenant c81d4516,
                        5 Oct 2026) — a claim that a second user would find no
                        AI Calling seat, about a product held by the workspace
                        and assigned to nobody. It says so instead, and offers
                        no usage bar or free count to misread. */}
                    <TableCell data-testid={`licence-seats-${license.license_code}`}>
                      {isTenantLevel(license) ? (
                        <span className="font-medium">{TENANT_LEVEL_LABEL}</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{license.seats_purchased}</span>
                          <span className="text-slate-400">seats</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell data-testid={`licence-usage-${license.license_code}`}>
                      {isTenantLevel(license) ? (
                        <span className="text-xs text-slate-500">Not assigned to users</span>
                      ) : (
                        <div className="w-32">
                          <div className="flex justify-between text-xs mb-1">
                            <span>{license.seats_assigned} used</span>
                            <span data-testid={`licence-free-${license.license_code}`}>{availabilityText(license)}</span>
                          </div>
                          <Progress
                            value={license.seats_purchased > 0
                              ? (license.seats_assigned / license.seats_purchased) * 100
                              : 0
                            }
                            className="h-2"
                          />
                        </div>
                      )}
                    </TableCell>
                    <TableCell data-testid={`licence-available-${license.license_code}`}>
                      {isTenantLevel(license)
                        ? <span className="text-slate-400">—</span>
                        : (seats(license).available ?? <span className="text-slate-400">—</span>)}
                      {/* SHOWN, not silently corrected. A row whose own numbers
                          contradict each other is a commercial fact somebody
                          needs to look at; clamping it to look tidy hides an
                          over-assignment from the only people who can fix it. */}
                      {seatInconsistency(license) && (
                        <div className="text-xs text-amber-700 mt-1"
                             data-testid={`licence-seat-warning-${license.license_code}`}>
                          {seatInconsistency(license)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1"
                           data-testid={`licence-price-${license.license_code}`}>
                        {priceLabel(license)}
                        {/* The column is "Price/Seat"; this price is not. */}
                        {isTenantLevel(license) && license.final_price != null && (
                          <span className="text-xs text-slate-500">per tenant</span>
                        )}
                        {license.override_price != null && (
                          <Badge variant="outline" className="ml-1 text-xs">Override</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{lineTotalLabel(license)}</span>
                    </TableCell>
                    <TableCell>
                      {license.status === 'active' ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge variant="secondary">{license.status}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGate permission="license:write">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(license)}
                            data-testid={`edit-tenant-license-${license.license_code}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission="license:write">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveLicense(license)}
                            className="text-red-600 hover:text-red-700"
                            disabled={license.seats_assigned > 0}
                            data-testid={`remove-tenant-license-${license.license_code}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add License Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add License to Tenant</DialogTitle>
            <DialogDescription>
              Select a license type and configure the seat count
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>License Type *</Label>
              <Select
                value={formData.license_id}
                onValueChange={(v) => setFormData({ ...formData, license_id: v })}
              >
                <SelectTrigger data-testid="license-type-select">
                  <SelectValue placeholder="Select license..." />
                </SelectTrigger>
                <SelectContent>
                  {availableLicenses.map(license => (
                    <SelectItem key={license.id} value={license.id}>
                      {/* "Not priced" for a catalogue row with no price — never "$/seat". */}
                      {catalogueOptionLabel(license)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {addingTenantLevel ? (
              <p className="text-sm text-slate-600" data-testid="add-licence-tenant-level">
                {TENANT_LEVEL_LABEL} — one licence for the tenant, not assigned to users.
              </p>
            ) : (
            <div className="space-y-2">
              <Label>Seats to Purchase *</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, seats_purchased: Math.max(1, prev.seats_purchased - 1) }))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  min="1"
                  value={formData.seats_purchased}
                  onChange={(e) => setFormData({ ...formData, seats_purchased: parseInt(e.target.value) || 1 })}
                  className="w-20 text-center"
                  data-testid="seats-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, seats_purchased: prev.seats_purchased + 1 }))}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <Label>Override Default Price</Label>
                <p className="text-xs text-slate-500">Set a custom price for this tenant</p>
              </div>
              <Switch
                checked={formData.use_override}
                onCheckedChange={(checked) => setFormData({ ...formData, use_override: checked })}
              />
            </div>

            {formData.use_override && (
              <div className="space-y-2">
                <Label>Price Per Seat</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.override_price || ''}
                    onChange={(e) => setFormData({ ...formData, override_price: parseFloat(e.target.value) || 0 })}
                    className="pl-9"
                    data-testid="override-price-input"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddLicense}
              disabled={saving || !formData.license_id}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-add-license-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Add License
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit License Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowEditDialog(false);
          setSelectedLicense(null);
          resetForm();
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit License: {selectedLicense?.license_name}</DialogTitle>
            <DialogDescription>
              {isTenantLevel(selectedLicense)
                ? 'Update pricing. This licence is held by the whole workspace and has no seat count.'
                : `Update seat count or pricing. Cannot reduce below assigned seats (${selectedLicense?.seats_assigned || 0}).`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {isTenantLevel(selectedLicense) ? (
              <p className="text-sm text-slate-600" data-testid="edit-licence-tenant-level">
                {TENANT_LEVEL_LABEL} — not assigned to users.
              </p>
            ) : (
            <div className="space-y-2">
              <Label>Seats Purchased *</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    seats_purchased: Math.max(selectedLicense?.seats_assigned || 1, prev.seats_purchased - 1) 
                  }))}
                  disabled={formData.seats_purchased <= (selectedLicense?.seats_assigned || 1)}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  min={selectedLicense?.seats_assigned || 1}
                  value={formData.seats_purchased}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    seats_purchased: Math.max(selectedLicense?.seats_assigned || 1, parseInt(e.target.value) || 1) 
                  })}
                  className="w-20 text-center"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, seats_purchased: prev.seats_purchased + 1 }))}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {selectedLicense?.seats_assigned > 0 && (
                <p className="text-xs text-amber-600">
                  Minimum seats: {selectedLicense.seats_assigned} (currently assigned)
                </p>
              )}
            </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <Label>Override Default Price</Label>
                <p className="text-xs text-slate-500">
                  {/* An unpriced licence has no default; zero is a decision. */}
                  Default: {defaultPriceLabel(selectedLicense)}
                </p>
              </div>
              <Switch
                checked={formData.use_override}
                onCheckedChange={(checked) => setFormData({ ...formData, use_override: checked })}
              />
            </div>

            {formData.use_override && (
              <div className="space-y-2">
                <Label>Price Per Seat</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.override_price || ''}
                    onChange={(e) => setFormData({ ...formData, override_price: parseFloat(e.target.value) || 0 })}
                    className="pl-9"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditDialog(false);
              setSelectedLicense(null);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateLicense}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-edit-license-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Update License
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantLicensesTab;
